// src/tasks/services/use-clinical-tasks.ts
// Task management R1: "My tasks" and "Team queue" reads, and the writes (create, claim, complete, reassign), over the
// Scrips.Patient clinical task store. Bodies are parsed at the boundary (`@/tasks/lib/clinical-task-wire`).
//
// LIVE UPDATE: both lists are read again every TASKS_POLL_MS (30 s) while the page is open, on window focus, and at
// once after any write this browser makes. (The store also publishes ClinicalTaskChanged for a SignalR push, PROD-2731;
// R1 polls, the push is R2.)
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, post } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import { toast } from 'sonner';
import { isWaiting, parseExecutionLog, parseRunInstance, tickConfirmed } from '@/patients/lib/care-model-tasks';
import { completeChecklistItem, fetchExecutionLog, fetchWorkflowInstance } from '@/encounter/services/care-model-run-api';
import { careModelQueueKey } from '@/patients/services/use-care-model-tasks';
import { describeTaskError, parseQueuePage, parseTask, type ClinicalTask, type QueuePage, type TaskRole } from '@/tasks/lib/clinical-task-wire';

export const TASKS_POLL_MS = 30_000;
export const QUEUE_PAGE_SIZE = 200;

export type TaskList = 'mine' | 'team';

/** Keyed on the signed-in user and organisation, so a cached list never shows to the next person signed in on this browser. */
export const tasksKey = (list?: TaskList, who?: string) => (list ? (['clinical-tasks', who ?? '', list] as const) : (['clinical-tasks'] as const));

/** At most this many pages (200 each) are read; beyond it the page says the list was cut. */
export const QUEUE_MAX_PAGES = 5;

/** mine = the live tasks I own, any role; team = every live task in the practice, any role (claimed ones show their owner). */
export function queuePath(list: TaskList, page = 1): string {
  const qs = new URLSearchParams({ queue: 'all', status: 'live', page: String(page), pageSize: String(QUEUE_PAGE_SIZE) });
  if (list === 'mine') qs.set('assignee', 'me');
  return `/api/Patients/Tasks/Queue?${qs.toString()}`;
}

export interface TaskQueue extends QueuePage {
  /** More rows exist than were read (over QUEUE_MAX_PAGES pages). */
  truncated: boolean;
}

export async function fetchQueue(list: TaskList, signal?: AbortSignal): Promise<TaskQueue> {
  const items: QueuePage['items'] = [];
  let total = 0;
  for (let n = 1; n <= QUEUE_MAX_PAGES; n++) {
    const page = parseQueuePage(await get<unknown>(queuePath(list, n), { signal }));
    if (!page) throw new Error('task queue: unreadable page');
    total = page.total;
    items.push(...page.items);
    if (items.length >= total) return { total, items, truncated: false };
    // A short page below the declared total: the read is incomplete, never a shorter list.
    if (page.items.length < QUEUE_PAGE_SIZE) return { total, items, truncated: true };
  }
  return { total, items, truncated: items.length < total };
}

export function useTaskQueue(list: TaskList, enabled = true) {
  const who = useAuthStore((s) => `${s.user?.organizationId ?? ''}|${s.user?.userId ?? ''}`);
  return useQuery<TaskQueue>({
    queryKey: tasksKey(list, who),
    enabled,
    queryFn: ({ signal }) => fetchQueue(list, signal),
    refetchInterval: TASKS_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

const taskPath = (patientId: string, taskId?: string, act?: string) =>
  `/api/Patients/${encodeURIComponent(patientId)}/Tasks${taskId ? `/${encodeURIComponent(taskId)}` : ''}${act ? `/${act}` : ''}`;

async function written(body: unknown): Promise<ClinicalTask> {
  const task = parseTask(body);
  if (!task) throw new Error('task: unreadable answer');
  return task;
}

export type TaskAction =
  | { kind: 'claim'; task: ClinicalTask }
  | { kind: 'complete'; task: ClinicalTask }
  | { kind: 'reassign'; task: ClinicalTask; performerType: TaskRole }
  | { kind: 'start'; task: ClinicalTask }
  | { kind: 'complete-care-model'; task: ClinicalTask; userId: string | null | undefined; verifyStep?: boolean };

/** The care model did not confirm the item: the task stays open (claimed by you) and nothing is recorded as done. */
export class CareModelTickError extends Error {
  constructor() {
    super('care-model tick not confirmed');
    this.name = 'CareModelTickError';
  }
}

/** True when the run still waits on the step (or cannot be read: fail closed). */
/**
 * POSITIVE evidence that the run finished this step: its log holds an outcome for the step ("Complete"). A run that
 * faulted or was cancelled mid-step, a step still waiting, or an unreadable run is NOT evidence (fail closed).
 */
async function stepCompleted(run: { instanceId: string; activityId: string }): Promise<boolean> {
  try {
    const [instance, log] = await Promise.all([fetchWorkflowInstance(run.instanceId), fetchExecutionLog(run.instanceId)]);
    const i = parseRunInstance(instance);
    const l = parseExecutionLog(log);
    if (!i || !l || isWaiting(i, l, run.activityId)) return false;
    return l.some((e) => e.activityId === run.activityId && e.outcomes.includes('Complete'));
  } catch {
    return false;
  }
}

/** The tick failed AND the undo failed: the store says done, the care model does not. Said plainly; never "still open". */
export class CareModelReconcileError extends Error {
  constructor() {
    super('care-model task completed but tick and undo both failed');
    this.name = 'CareModelReconcileError';
  }
}

/** Read one task again (after a refusal, to learn its real state). */
export async function fetchTask(patientId: string, taskId: string): Promise<ClinicalTask> {
  return written(await get<unknown>(taskPath(patientId, taskId)));
}

/** A store refusal is a no-op success only when a fresh read shows the task really completed (the engine closed it first). */
async function completedElsewhere(task: ClinicalTask, error: unknown): Promise<ClinicalTask> {
  if (!(error instanceof ApiError) || (error.status !== 409 && error.status !== 403)) throw error;
  const now = await fetchTask(task.patientId, task.id).catch(() => null);
  if (now?.status === 'completed') return now;
  throw error;
}

/**
 * PROD-3283: completing a care-model task resumes its run. The STORE WRITE ALWAYS COMES FIRST, so no engine side effect runs
 * without the store's current authorisation (owner + Task_Write, or Task_Manage):
 *   1. read the task fresh (a listed owner, status or version may be stale);
 *   2. CLAIM it if it is not mine (the store checks the role queue / Task_Manage), then COMPLETE it in the store;
 *   3. TICK the engine's item (the call the patient record and the studio make): the run resumes, and the engine's own close
 *      of this task is a no-op (already completed);
 *   4. if the engine does not confirm the tick, REOPEN the task (the completer's undo, inside the store's undo window) and
 *      say so: nothing is left recorded as done that the care model did not record.
 * `verifyStep`: the page has no current read of this step, so its checklist guards cannot be applied: the store task is
 * closed only on POSITIVE evidence that THIS run completed the step, and the engine is not ticked.
 */
export async function completeCareModelTask(task: ClinicalTask, userId: string | null | undefined, verifyStep = false): Promise<ClinicalTask> {
  let current = await fetchTask(task.patientId, task.id);
  if (current.status === 'completed') return current;
  if (!['requested', 'accepted', 'in-progress'].includes(current.status)) throw new ApiError(409, JSON.stringify({ code: 'invalid-transition' }));
  const run = current.run ?? task.run;
  if (run && verifyStep && !(await stepCompleted(run))) throw new CareModelTickError();
  // A requested task is claimed even when it names me (a reopened task keeps its owner but must be claimed again).
  if (current.status === 'requested' || !current.ownerUserId || current.ownerUserId.toLowerCase() !== (userId ?? '').toLowerCase()) {
    try {
      current = await sendTaskAction({ kind: 'claim', task: current });
    } catch (e) {
      return completedElsewhere(task, e);
    }
  }
  let done: ClinicalTask;
  try {
    done = await sendTaskAction({ kind: 'complete', task: current });
  } catch (e) {
    if (e instanceof ApiError) return completedElsewhere(current, e);
    // No answer (network): the Complete may have committed. Read it: committed -> go on to the tick; otherwise say it failed.
    const now = await fetchTask(current.patientId, current.id).catch(() => null);
    if (now?.status !== 'completed') throw e;
    done = now;
  }
  if (!run || verifyStep) return done;
  let confirmed = false;
  try {
    confirmed = tickConfirmed(await completeChecklistItem(run.instanceId, run.activityId, run.label));
  } catch {
    confirmed = false;
  }
  if (!confirmed) {
    const reopened = await post<unknown>(taskPath(done.patientId, done.id, 'Reopen'), { version: done.version, reason: 'care-model-tick-not-confirmed' })
      .then(written)
      .catch(() => null);
    if (reopened?.status !== 'requested') throw new CareModelReconcileError();
    throw new CareModelTickError();
  }
  return done;
}

export function sendTaskAction(a: TaskAction): Promise<ClinicalTask> {
  const { task } = a;
  if (a.kind === 'complete-care-model') return completeCareModelTask(task, a.userId, a.verifyStep);
  if (a.kind === 'start') return post<unknown>(taskPath(task.patientId, task.id, 'Start'), { version: task.version }).then(written);
  if (a.kind === 'reassign')
    return post<unknown>(taskPath(task.patientId, task.id, 'Reassign'), { version: task.version, performerType: a.performerType }).then(written);
  return post<unknown>(taskPath(task.patientId, task.id, a.kind === 'claim' ? 'Claim' : 'Complete'), { version: task.version }).then(written);
}

/** One write at a time per row. Success or a refusal both read the lists again (a version conflict means they are stale). */
export function useTaskAction() {
  const qc = useQueryClient();
  return useMutation<ClinicalTask, Error, TaskAction>({
    mutationFn: sendTaskAction,
    retry: false,
    meta: { skipGlobalError: true },
    // Every refusal is said here, before the lists are read again: the row may vanish on that refresh (a task completed
    // or changed elsewhere leaves the live list), and a per-call callback would then never run. A reconciliation failure
    // stays up longer, so its recovery step is never lost.
    onError: (error) => {
      if (error instanceof CareModelReconcileError) toast.error(describeTaskError(error), { duration: 20_000 });
      else toast.error(describeTaskError(error));
    },
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: tasksKey() }), qc.invalidateQueries({ queryKey: careModelQueueKey })]),
  });
}

export interface CreatedTask {
  task: ClinicalTask;
  /** "Me" was asked, the task was created, but the claim failed: it waits in the role queue. Never re-create it. */
  claimFailed: boolean;
}

export interface CreateTaskVars {
  patientId: string;
  body: Record<string, unknown>;
  /** Claim it at once: "assign to me". */
  claim: boolean;
}

export async function createTask({ patientId, body, claim }: CreateTaskVars): Promise<CreatedTask> {
  const created = await written(await post<unknown>(taskPath(patientId), body));
  if (!claim) return { task: created, claimFailed: false };
  try {
    return { task: await sendTaskAction({ kind: 'claim', task: created }), claimFailed: false };
  } catch {
    return { task: created, claimFailed: true };
  }
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation<CreatedTask, Error, CreateTaskVars>({
    mutationFn: createTask,
    retry: false,
    meta: { skipGlobalError: true },
    onSettled: () => qc.invalidateQueries({ queryKey: tasksKey() }),
  });
}
